import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimes, faHeart, faUser, faCalendar, faDollarSign } from '@fortawesome/free-solid-svg-icons';

const ClaimSuccessModal = ({ requestData, nameRequest, type = 'name', onClose }) => {
  // Support both old nameRequest prop and new requestData prop
  const data = requestData || nameRequest;
  console.log('🎯 ClaimSuccessModal: Rendering with:', { type, data, hasData: !!data });
  if (!data) return null;

  const isPledge = type === 'pledge';

  // Parse recipient name if it's a JSON array
  const parseRecipientName = (name) => {
    if (!name) return '';
    try {
      if (name.trim().startsWith('[')) {
        const namesArray = JSON.parse(name);
        if (Array.isArray(namesArray) && namesArray.length > 0) {
          return namesArray.join(', ');
        }
      }
    } catch (e) {
      // Not JSON, return as is
    }
    return name;
  };

  const recipientName = isPledge 
    ? parseRecipientName(data.recipient_names || data.prayer_recipient_name)
    : parseRecipientName(data.recipient_name || data.prayer_recipient_name);
    
  const submitterName = data.submitter_first_name && data.submitter_last_name
    ? `${data.submitter_first_name} ${data.submitter_last_name}`
    : data.contact_name || data.user_first_name && data.user_last_name
    ? `${data.user_first_name} ${data.user_last_name}`
    : 'A community member';

  const pledgeAmount = isPledge ? data.pledge_amount : null;
  const pledgeMessage = isPledge ? data.pledge_message : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {isPledge ? 'Pledge Successfully Recorded' : 'Name Successfully Added'}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Pledge Amount or Recipient Name */}
          {isPledge ? (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
              <div className="flex items-center justify-center gap-3">
                <p className="text-[32px] font-bold text-gray-900">
                  ${pledgeAmount}
                </p>
              </div>
              {recipientName && recipientName !== 'General Donation' && (
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">In honor of</p>
                  <p className="text-lg font-semibold text-gray-700">{recipientName}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <FontAwesomeIcon icon={faHeart} className="text-red-500 text-xl" />
                </div>
                <div className="flex-1">
                  <p className="text-[22px] font-bold text-gray-900">
                    {recipientName}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submitter Info */}
          <div className="flex items-center gap-3 px-2">
            <FontAwesomeIcon icon={faUser} className="text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Added by</p>
              <p className="text-sm font-medium text-gray-700">{submitterName}</p>
            </div>
          </div>

          {/* Date Info */}
          <div className="flex items-center gap-3 px-2">
            <FontAwesomeIcon icon={faCalendar} className="text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Date added</p>
              <p className="text-sm font-medium text-gray-700">
                {new Date(data.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>

          {/* Intention Type (for names) or Pledge Message (for pledges) */}
          {!isPledge && data.intention_type && (
            <div className="bg-red-50 rounded-lg p-3 border border-red-100">
              <p className="text-xs text-red-600 font-medium mb-1">Prayer Intention</p>
              <p className="text-sm text-red-800">
                {data.intention_type === 'Refuah' ? 'Healing & Recovery' : data.intention_type}
              </p>
            </div>
          )}

          {isPledge && pledgeMessage && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-600 font-medium mb-1">Message</p>
              <p className="text-sm text-gray-700">
                {pledgeMessage}
              </p>
            </div>
          )}

          {/* Custom Message/Notes */}
          {!isPledge && data.custom_intention && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-600 font-medium mb-1">Additional Notes</p>
              <p className="text-sm text-gray-700">
                {data.custom_intention}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {isPledge ? 'Manage Pledges' : 'Manage Prayer List'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClaimSuccessModal;
